import { format, formatDistanceToNow, isValid } from "date-fns";

// ========= Options =========

export const TASK_STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "On Hold", value: "HOLD" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export const TASK_PRIORITY_OPTIONS = [
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

// ========= Colors =========

export const getTaskStatusColor = (status) => {
  switch (
    String(status || "")
      .toLowerCase()
      .replace(/\s+/g, "_")
  ) {
    case "completed":
      return "bg-app-status-completed-bg text-app-status-completed-text";
    case "in_progress":
      return "bg-app-status-in-progress-bg text-app-status-in-progress-text";
    case "hold":
      return "bg-app-status-hold-bg text-app-status-hold-text";
    case "pending":
      return "bg-app-status-pending-bg text-app-status-pending-text";
    case "cancelled":
      return "bg-app-status-cancelled-bg text-app-status-cancelled-text";
    default:
      return "bg-app-surface-muted text-app-text";
  }
};

export const getTaskPriorityColor = (priority) => {
  switch (String(priority || "").toLowerCase()) {
    case "high":
      return "bg-app-priority-high-bg text-app-priority-high-text";
    case "medium":
      return "bg-app-priority-medium-bg text-app-priority-medium-text";
    case "low":
      return "bg-app-priority-low-bg text-app-priority-low-text";
    default:
      return "bg-app-surface-muted text-app-text";
  }
};

/** Solid dot color (not bg/text pair) for compact indicators like the Kanban priority stripe. */
export const getTaskPriorityDotColor = (priority) => {
  switch (String(priority || "").toLowerCase()) {
    case "high":
      return "var(--app-priority-high-text)";
    case "medium":
      return "var(--app-priority-medium-text)";
    case "low":
      return "var(--app-priority-low-text)";
    default:
      return "var(--app-text-muted)";
  }
};

/** Solid dot color for a task status, used by Kanban column headers. */
export const getTaskStatusDotColor = (status) => {
  switch (
    String(status || "")
      .toLowerCase()
      .replace(/\s+/g, "_")
  ) {
    case "completed":
      return "var(--app-status-completed-text)";
    case "in_progress":
      return "var(--app-status-in-progress-text)";
    case "hold":
      return "var(--app-status-hold-text)";
    case "pending":
      return "var(--app-status-pending-text)";
    case "cancelled":
      return "var(--app-status-cancelled-text)";
    default:
      return "var(--app-text-muted)";
  }
};

export const getAvatarColor = (seed) => {
  const palette = [
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#06b6d4",
    "#ef4444",
    "#6366f1",
  ];
  const str = String(seed || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

// ========= People helpers =========

/** Normalize any user-ish object to a compact { id, name, email } shape. */
export const normalizeTaskUser = (user) => {
  if (!user) return null;
  if (typeof user === "number" || typeof user === "string") {
    return { id: user, name: null, email: null };
  }
  return {
    id: user.id ?? user.userId ?? null,
    name: user.name || user.email || "Unknown",
    email: user.email || null,
  };
};

export const getTaskOwner = (task) => normalizeTaskUser(task?.assignedTo);

/**
 * Returns the full people list on a task (owner + additional assignees), deduped by id/email.
 * Falls back gracefully when the backend hasn't started returning `assignees` yet.
 */
export const getTaskAssignees = (task) => {
  const owner = getTaskOwner(task);
  const extra = Array.isArray(task?.assignees)
    ? task.assignees.map(normalizeTaskUser)
    : Array.isArray(task?.additionalAssignees)
    ? task.additionalAssignees.map(normalizeTaskUser)
    : [];
  const all = [owner, ...extra].filter(Boolean);
  const seen = new Set();
  return all.filter((u) => {
    const key = u.id ?? u.email ?? u.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const isUserOnTask = (task, email) => {
  if (!email) return false;
  return getTaskAssignees(task).some((u) => u.email === email);
};

// ========= Subtasks =========

export const getSubtasks = (task) => (Array.isArray(task?.subtasks) ? task.subtasks : []);

export const getSubtaskProgress = (task) => {
  const subtasks = getSubtasks(task);
  const total = subtasks.length;
  const done = subtasks.filter((s) => s?.completed || s?.status === "DONE").length;
  return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
};

/** Resolve the person a subtask is assigned to, if any. */
export const getSubtaskAssignee = (subtask) => {
  if (!subtask) return null;
  if (subtask.assignee || subtask.assignedTo) {
    return normalizeTaskUser(subtask.assignee || subtask.assignedTo);
  }
  if (subtask.assigneeId != null) {
    return { id: subtask.assigneeId, name: subtask.assigneeName || null, email: null };
  }
  return null;
};

/** Lightweight overdue/due-soon check for a subtask's own deadline. */
export const getSubtaskDeadlineInfo = (subtask) => {
  if (!subtask?.deadline || subtask?.completed) return null;
  const deadline = new Date(subtask.deadline);
  if (!isValid(deadline)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deadline.setHours(0, 0, 0, 0);
  const days = Math.round((deadline - today) / (1000 * 60 * 60 * 24));
  return { days, isPassed: days < 0, isDueSoon: days >= 0 && days <= 2 };
};

// ========= Dates =========

export {
  formatDisplayDate as formatTaskDate,
  formatDisplayDateTime as formatTaskDateTime,
} from "utils/dateFormat";

export const formatRelativeTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (!isValid(d)) return "";
  try {
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return "";
  }
};

/** Turns a day count from `getDeadlineInfo` into a plain "how long left" phrase. */
export const getDaysLeftLabel = (days) => {
  if (days == null || Number.isNaN(days)) return "";
  if (days === 0) return "Due today";
  if (days === 1) return "1 day left";
  if (days > 1) return `${days} days left`;
  const overdueBy = Math.abs(days);
  return overdueBy === 1 ? "1 day overdue" : `${overdueBy} days overdue`;
};

export const getDeadlineInfo = (task) => {
  if (!task?.deadline) return null;
  if (task?.status === "COMPLETED" || task?.status === "CANCELLED") return null;
  const deadline = new Date(task.deadline);
  if (!isValid(deadline)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDay = new Date(deadline);
  deadlineDay.setHours(0, 0, 0, 0);
  const days = Math.round((deadlineDay - today) / (1000 * 60 * 60 * 24));
  return { days, isPassed: days < 0, isDueSoon: days >= 0 && days <= 2 };
};

// ========= Mentions =========

const MENTION_REGEX = /@([A-Za-z][\w'.-]*(?:\s[A-Za-z][\w'.-]*)?)/g;

/**
 * Given free text containing `@Name` tokens and the list of users available for mention,
 * returns the resolved list of unique user ids that were actually mentioned.
 */
export const extractMentionIds = (text, users = []) => {
  if (!text || !users?.length) return [];
  const ids = new Set();
  const matches = text.matchAll(MENTION_REGEX);
  for (const match of matches) {
    const name = match[1]?.trim().toLowerCase();
    if (!name) continue;
    const user = users.find((u) => u.name?.trim().toLowerCase() === name);
    if (user?.id != null) ids.add(user.id);
  }
  return Array.from(ids);
};

/**
 * Splits comment text into plain/mention segments for rendering, so `@Name` can be highlighted.
 * Returns an array of { text, isMention } chunks.
 */
export const renderMentionSegments = (text) => {
  if (!text) return [];
  const segments = [];
  let lastIndex = 0;
  const matches = text.matchAll(MENTION_REGEX);
  for (const match of matches) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), isMention: false });
    }
    segments.push({ text: match[0], isMention: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isMention: false });
  }
  return segments;
};

// ========= Unified activity timeline =========

/**
 * Merges system changeLogs and discussion comments into a single chronological feed.
 * Each item: { kind: 'event' | 'comment', date, ...original }
 */
export const buildActivityItems = (changeLogs = [], comments = []) => {
  const events = (changeLogs || []).map((log) => ({
    kind: "event",
    date: log?.changedAt,
    ...log,
  }));
  const commentItems = (comments || []).map((comment) => ({
    kind: "comment",
    date: comment?.date ?? comment?.createdDate,
    ...comment,
  }));
  return [...events, ...commentItems].sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const defaultActivityMessage = (event) => {
  switch (event?.action) {
    case "ASSIGNED":
      return "Task assigned";
    case "REASSIGNED":
      return "Task reassigned";
    case "COMPLETED":
      return "Task marked completed";
    case "STATUS_CHANGED":
      return "Status updated";
    case "PRIORITY_CHANGED":
      return "Priority updated";
    default:
      return event?.message || "Task updated";
  }
};

export const ADMIN_ROLES = ["ADMIN", "SUPER_ADMIN"];

export const isAdminRole = (user) =>
  ADMIN_ROLES.includes(String(user?.type || user?.role || "").toUpperCase());

/**
 * People on a task, re-resolved against `directory`.
 *
 * Task DTOs carry thin people (id/name/email only), so anything role-aware has to look the
 * person up in the org user list — the only source with a role on it. Deduped by id.
 */
export const resolveTaskPeople = ({ people = [], directory = [], excludeId, excludeEmail }) => {
  const byId = new Map();
  directory.forEach((user) => {
    if (user?.id != null) byId.set(user.id, user);
  });

  const seen = new Set();
  return people
    .map((person) => (person?.id != null ? byId.get(person.id) ?? person : null))
    .filter((user) => {
      if (!user?.id || seen.has(user.id)) return false;
      if (excludeId != null && user.id === excludeId) return false;
      if (excludeEmail && user.email === excludeEmail) return false;
      seen.add(user.id);
      return true;
    });
};

/**
 * Who a subtask may be assigned to: the assigner, the primary assignee and the collaborators.
 * Mirrors TaskAccessService.assignableIds on the backend — assigning a subtask must not pull
 * someone onto a task they were never added to.
 */
export const getTaskAssignableUsers = ({ people = [], directory = [] }) =>
  resolveTaskPeople({ people, directory });

/**
 * Admins/super-admins on a task who would actually receive an escalation. The raiser is dropped
 * because the backend never notifies whoever flagged the task.
 */
export const getTaskAdminRecipients = ({
  participants = [],
  directory = [],
  excludeId,
  excludeEmail,
}) =>
  resolveTaskPeople({ people: participants, directory, excludeId, excludeEmail }).filter(
    isAdminRole
  );
